import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { m } from 'motion/react';
import { ChevronDown, ChevronRight, LogOut, Menu as MenuIcon, Settings } from 'lucide-react';
import { useEffect, useState } from 'react';

import { auth } from '../api/endpoints';
import { AbsorMark } from '../components/Brand';
import { Menu, MenuItem, MenuLabel, MenuSeparator } from '../components/Menu';
import { SkeletonRows } from '../components/Skeleton';
import { LanguagePicker, ThemeChoices, ThemeToggle } from '../components/Switchers';
import { NotificationsBell } from '../features/notifications/NotificationsBell';
import { useT } from '../i18n/provider';
import { cn } from '../lib/cn';
import { useAuth } from '../stores/auth';

import { CompanyNav } from './nav/CompanyNav';
import { GlobalNav } from './nav/GlobalNav';
import { Sidebar } from './nav/Sidebar';
import { findSection } from './nav/sections';

/**
 * El marco en el que vive toda pantalla con sesión.
 *
 * También es la puerta del arranque: hasta que el store de sesión dice `ready`,
 * la aplicación pinta un esqueleto en lugar de adivinar. El token de acceso
 * vive solo en memoria (ver el store de sesión), así que una recarga dura
 * genuinamente no tiene sesión durante los pocos cientos de milisegundos que
 * tarda el refresco — y `13 §6` pide esqueletos, nunca un spinner a pantalla
 * completa.
 *
 * La estructura —barra lateral oscura fija, cabecera pegajosa translúcida— es
 * la del resto de productos Absor. Que la navegación sea casi negra en los dos
 * temas no es un descuido: es el ancla de marca, lo único que no cambia cuando
 * el usuario conmuta el tema.
 *
 * Lo que sí cambia es **qué** hay dentro de la barra: dentro de una empresa la
 * ocupan sus veintiuna secciones, y fuera, el espacio de trabajo. El marco solo
 * decide cuál de las dos monta.
 */
export function AppShell() {
  const { ready, user, accessToken } = useAuth();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const t = useT();
  const [mobileOpen, setMobileOpen] = useState(false);

  /**
   * La redirección vive aquí y no solo en el `beforeLoad` de la ruta.
   *
   * `beforeLoad` corre una vez, en el momento de navegar — y en ese momento el
   * refresco del arranque no ha terminado, así que `ready` es falso y la guarda
   * declina decidir, correctamente. Nada la vuelve a ejecutar cuando `ready`
   * cambia, lo que dejaba a un visitante sin autenticar sentado en el marco de
   * la aplicación para siempre. Este efecto es la mitad que reacciona a que la
   * respuesta llegue.
   */
  useEffect(() => {
    if (ready && !accessToken) {
      void navigate({ to: '/login' });
    }
  }, [ready, accessToken, navigate]);

  // Navegar cierra el cajón en móvil. Sin esto, tocar un enlace deja el menú
  // abierto encima de la pantalla que se acaba de pedir.
  useEffect(() => setMobileOpen(false), [pathname]);

  if (!ready || !accessToken) {
    return (
      <div className="mx-auto max-w-5xl p-8">
        <SkeletonRows rows={4} />
      </div>
    );
  }

  /*
    Qué empresa, leída del camino y no de `useParams`.

    La cabecera y la barra viven por encima de la ruta de empresa, así que no
    tienen sus parámetros; el camino sí lo tienen siempre. Resolver el slug
    contra la lista del token —y no preguntando a la API— es además lo que hace
    que un slug ajeno se comporte igual que uno inexistente.
  */
  const companySlug = /^\/c\/([^/]+)/.exec(pathname)?.[1];
  const company = user?.companies.find((item) => item.slug === companySlug);

  /*
    Quién necesita más que el ancho de lectura.

    El organigrama, porque un árbol no se dobla. Y el detalle de tarea, porque
    su traza de ejecución son rutas de fichero y órdenes de consola —
    `Edit src/Controller/AuthController.php`, `vendor/bin/phpunit --filter
    Google`— y a `max-w-5xl`, con la columna de ejecuciones al lado, se cortaban
    todas por la mitad. Una línea de log truncada no es una línea de log.

    Solo el detalle: `/tasks/new` es un formulario y un formulario a ciento diez
    ems son etiquetas a un lado y campos al otro.
  */
  const wide = pathname.endsWith('/org') || /\/tasks\/\d+$/.test(pathname);

  const signOut = async () => {
    try {
      await auth.logout();
    } finally {
      // El estado local se limpia diga lo que diga el servidor: un cierre de
      // sesión fallido no puede dejar al usuario con aspecto de seguir dentro.
      useAuth.getState().signOut();
      void navigate({ to: '/login' });
    }
  };

  const initials = (user?.name ?? user?.email ?? '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="min-h-screen bg-canvas">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-100 focus:rounded-sm focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:text-white"
      >
        {t('nav.skipToContent')}
      </a>

      <Sidebar open={mobileOpen} onClose={closeMobile}>
        {company ? (
          <CompanyNav
            companySlug={company.slug}
            companyName={company.name}
            pathname={pathname}
            onNavigate={closeMobile}
          />
        ) : (
          <GlobalNav pathname={pathname} onNavigate={closeMobile} />
        )}
      </Sidebar>

      <div className="lg:pl-sidebar">
        <header className="sticky top-0 z-30 flex h-topbar items-center gap-2 border-b border-line bg-paper/85 px-4 backdrop-blur-lg sm:px-6 lg:px-10">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label={t('nav.openSidebar')}
            className="grid size-9 shrink-0 place-items-center rounded-sm text-ink-soft transition-colors hover:bg-muted hover:text-ink lg:hidden"
          >
            {/*
              Dentro de una empresa el botón es un menú y nada más: la marca ya
              no está en la barra, así que un logotipo aquí no diría «abre la
              navegación», que es lo único que hace este botón.
            */}
            {company ? <MenuIcon className="size-5" /> : <AbsorMark className="size-5" />}
          </button>

          <Breadcrumb companySlug={companySlug} companyName={company?.name} pathname={pathname} />

          <div className="ml-auto flex items-center gap-1">
            <LanguagePicker />
            <ThemeToggle />
            <NotificationsBell />

            <Menu
              trigger={({ open, toggle }) => (
                <button
                  type="button"
                  onClick={toggle}
                  aria-expanded={open}
                  className="ml-1 flex items-center gap-2 rounded-sm py-1 pr-2 pl-1 transition-colors hover:bg-muted"
                >
                  <span className="brand-gradient grid size-8 shrink-0 place-items-center rounded-sm text-[0.7rem] font-extrabold text-white">
                    {initials}
                  </span>
                  <ChevronDown
                    className={cn('size-3.5 text-ink-faint transition-transform duration-fast', open && 'rotate-180')}
                    aria-hidden="true"
                  />
                </button>
              )}
            >
              {({ close }) => (
                <>
                  <div className="px-2.5 pt-1.5 pb-2">
                    <p className="truncate text-sm font-semibold text-ink">{user?.name}</p>
                    <p className="truncate text-xs text-ink-faint">{user?.email}</p>
                  </div>

                  <MenuSeparator />

                  <MenuLabel>{t('theme.label')}</MenuLabel>
                  <div className="px-1.5 pb-1.5">
                    <ThemeChoices />
                  </div>

                  <MenuSeparator />

                  <MenuItem
                    icon={<Settings />}
                    onClick={() => {
                      close();
                      void navigate({ to: '/settings' });
                    }}
                  >
                    {t('nav.account')}
                  </MenuItem>
                  <MenuItem
                    icon={<LogOut />}
                    className="text-danger hover:bg-danger-pale hover:text-danger"
                    onClick={() => {
                      close();
                      void signOut();
                    }}
                  >
                    {t('nav.signOut')}
                  </MenuItem>
                </>
              )}
            </Menu>
          </div>
        </header>

        {/*
          El organigrama necesita el ancho y ninguna otra pantalla lo necesita,
          así que el marco lee la ruta en vez de que cada pantalla pelee con el
          contenedor. Una pantalla que quisiera escaparse de `max-w-5xl` por su
          cuenta tendría que romper el elemento principal, que es la clase de
          CSS que luego nadie puede borrar.
        */}
        <main id="main" className={cn('mx-auto px-4 py-8 sm:px-6 lg:px-10', wide ? 'max-w-[110rem]' : 'max-w-5xl')}>
          {/*
            La transición de ruta anima **solo la entrada**, y la clave es el
            camino: al cambiar de ruta el bloque se remonta y entra desde abajo.

            Nada de `AnimatePresence` con `exit` aquí, aunque sea lo que pide el
            manual. `<Outlet/>` no es una foto: la copia saliente que
            `AnimatePresence` mantiene montada durante la salida vuelve a pintar
            **la ruta nueva**, así que durante esos milisegundos el formulario
            entero existe dos veces — dos `<input>` con el mismo `name`, dos
            etiquetas iguales y un lector de pantalla anunciándolo todo por
            duplicado. Se vio en el E2E, que se quejó de que «Nombre» resolvía a
            dos elementos. Un cross-fade honesto necesitaría congelar el árbol
            saliente, y no vale la pena: la entrada sola ya se lee como una
            transición.
          */}
          <m.div
            key={pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.24, ease: [0.22, 0.8, 0.28, 1] }}
          >
            <Outlet />
          </m.div>
        </main>
      </div>
    </div>
  );
}

/**
 * El rastro de la cabecera: dónde estoy, en una línea.
 *
 * Dentro de una empresa dice la **sección**, no la empresa: la empresa ya está
 * escrita en grande en la barra, y repetirla desperdiciaba la única línea que
 * hay para orientar a quien tiene la barra plegada. En pantallas anchas se
 * mantiene el nombre de la empresa delante, porque ahí sobra sitio y el par
 * empresa › sección se lee como un rastro de verdad.
 */
function Breadcrumb({
  companySlug,
  companyName,
  pathname,
}: {
  companySlug: string | undefined;
  companyName: string | undefined;
  pathname: string;
}) {
  const t = useT();

  if (companySlug === undefined || companyName === undefined) {
    return (
      <div className="flex min-w-0 items-center gap-2 text-sm">
        <span className="truncate font-semibold text-ink-soft">{t('nav.workspace')}</span>
      </div>
    );
  }

  const section = findSection(pathname, companySlug);

  return (
    <div className="flex min-w-0 items-center gap-1.5 text-sm">
      <Link
        to="/c/$companySlug"
        params={{ companySlug }}
        // Solo es «la página actual» cuando estás en el panel. Sin esto el
        // enrutador la marca en las veintiuna secciones —todas cuelgan de su
        // camino— y la sección de al lado deja de ser la única página actual.
        activeOptions={{ exact: true }}
        className="hidden shrink-0 items-center gap-2 truncate text-ink-soft transition-colors hover:text-ink sm:flex"
      >
        <span
          aria-hidden="true"
          className="brand-gradient grid size-6 shrink-0 place-items-center rounded-xs text-[0.6rem] font-extrabold text-white"
        >
          {companyName.charAt(0).toUpperCase()}
        </span>
        <span className="max-w-40 truncate">{companyName}</span>
      </Link>

      {section ? (
        <>
          <ChevronRight className="hidden size-3.5 shrink-0 text-ink-faint sm:block" aria-hidden="true" />
          <span className="truncate font-semibold text-ink">{t(section.label)}</span>
        </>
      ) : (
        <span className="truncate font-semibold text-ink sm:hidden">{companyName}</span>
      )}
    </div>
  );
}
